import axios from "axios";
import React, { useEffect, useState } from "react";
import { getAccessToken } from "../../../storage/storage";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { TableSkelton4 } from "../../../components/Skeletons";
import { FaChevronDown, FaChevronUp, FaMapMarkerAlt, FaRegCheckCircle, FaRegClock } from "react-icons/fa";

const Cleaners = () => {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedProperties, setExpandedProperties] = useState({});
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/api/user/properties-cleaners/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setData(response.data);
        // All properties collapsed by default
        const expanded = {};
        response.data.forEach(property => {
          expanded[property.id] = false;
        });
        setExpandedProperties(expanded);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching options:", error);
        setLoading(false);
      });
  }, []);

  const toggleProperty = (propertyId) => {
    setExpandedProperties(prev => ({
      ...prev,
      [propertyId]: !prev[propertyId]
    }));
  };

  const toggleSection = (propertyId, sectionType) => {
    setExpandedSections(prev => ({
      ...prev,
      [`${propertyId}-${sectionType}`]: !prev[`${propertyId}-${sectionType}`]
    }));
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toUpperCase()) {
      case "ACCEPTED":
        return "bg-green-100 text-green-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPropertyColor = (color) => {
    const colorMap = {
      "Pink": "#FF69B4",
      "Teal": "#008080",
      "Blue": "#0000FF",
      "Green": "#008000",
      "Purple": "#800080",
      "Orange": "#FFA500",
      "Red": "#FF0000",
      "Yellow": "#FFD700",
    };
    return colorMap[color] || "#6B7280"; // Default gray color
  };

  if (loading) {
    return (
      <>
        <h1 className="font-semibold text-2xl pb-4 pt-4">Cleaners</h1>
        <TableSkelton4 />
      </>
    );
  }

  return (
    <>
      <h1 className="font-semibold text-2xl pb-4 pt-4">Cleaners</h1>
      <p className="text-gray-600 mb-6">Manage your cleaning team by property</p>

      <div className="space-y-6">
        {data.length > 0 ? (
          data.map((property) => (
            <div key={property.id} className="border border-[#DFDFDF] bg-white rounded-lg shadow-md overflow-hidden">
              {/* Property Header - Collapsible */}
              <div 
                className="p-4 cursor-pointer flex items-center justify-between text-white"
                style={{ backgroundColor: getPropertyColor(property.property_color) }}
                onClick={() => toggleProperty(property.id)}
              >
                <div className="flex items-center space-x-4">
                  <FaMapMarkerAlt className="text-xl" />
                  <div>
                    <h2 className="text-xl font-semibold">{property.name}</h2>
                    <p className="text-white text-opacity-90 text-sm">
                      {property.acceptedCleanersCount + property.pendingCleanersCount} cleaners assigned
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-6">
                  {/* Accepted and Pending Counts */}
                  <div className="flex items-center space-x-4 text-sm">
                    <div className="flex items-center space-x-2 bg-white bg-opacity-20 px-3 py-1 rounded">
                      <FaRegCheckCircle className="" />
                      <span>{property.acceptedCleanersCount} Active</span>
                    </div>
                    <div className="flex items-center space-x-2 bg-white bg-opacity-20 px-3 py-1 rounded">
                      <FaRegClock className="" />
                      <span>{property.pendingCleanersCount} Pending</span>
                    </div>
                  </div>
                  
                  {/* Chevron Icon */}
                  <div className="flex items-center">
                    {expandedProperties[property.id] ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </div>
              </div>

              {/* Property Content */}
              {expandedProperties[property.id] && (
                <div className="p-6 space-y-6">
                  {/* Accepted Cleaners Section */}
                  {property.acceptedCleanersCount > 0 && (
                    <div className="border border-[#DFDFDF] rounded-lg overflow-hidden">
                      {/* Accepted Section Header */}
                      <div 
                        className="bg-green-100 p-4 cursor-pointer flex items-center justify-between"
                        onClick={() => toggleSection(property.id, 'accepted')}
                      >
                        <div className="flex items-center space-x-2">
                          <FaRegCheckCircle className="text-green-500" />
                          <h3 className="text-lg font-medium text-gray-900">
                            Accepted ({property.acceptedCleanersCount})
                          </h3>
                        </div>
                        <div className="flex items-center">
                          {expandedSections[`${property.id}-accepted`] ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                      </div>

                      {/* Accepted Cleaners Content */}
                      {expandedSections[`${property.id}-accepted`] && (
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="py-3 px-4 text-left font-medium text-gray-700">CLEANER</th>
                              <th className="py-3 px-4 text-left font-medium text-gray-700">EMAIL</th>
                              <th className="py-3 px-4 text-left font-medium text-gray-700">STATUS</th>
                              {/* <th className="py-3 px-4 text-left font-medium text-gray-700">ACTIONS</th> */}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {property.acceptedCleaners.map((cleaner, index) => (
                              <tr key={index}>
                                <td className="py-4 px-4">
                                  <div className="font-medium text-gray-900">
                                    {cleaner.first_name} {cleaner.surname}
                                  </div>
                                  {/* <div className="text-sm text-gray-500 mt-1">
                                    Last active: Recently
                                  </div> */}
                                </td>
                                <td className="py-4 px-4 text-gray-600">{cleaner.email}</td>
                                <td className="py-4 px-4">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(cleaner.status)}`}>
                                    {cleaner.status}
                                  </span>
                                </td>
                                {/* <td className="py-4 px-4">
                                  <button className="text-gray-400 hover:text-gray-600">
                                    ☑
                                  </button>
                                </td> */}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* Pending Cleaners Section */}
                  {property.pendingCleanersCount > 0 && (
                    <div className="border border-[#DFDFDF] rounded-lg overflow-hidden">
                      {/* Pending Section Header */}
                      <div 
                        className="bg-[#FFF5CE] p-4 cursor-pointer flex items-center justify-between"
                        onClick={() => toggleSection(property.id, 'pending')}
                      >
                        <div className="flex items-center space-x-2 ">
                          <FaRegClock className="text-yellow-500" />
                          <h3 className="text-lg font-medium text-gray-900">
                            Pending ({property.pendingCleanersCount})
                          </h3>
                        </div>
                        <div className="flex items-center ">
                          {expandedSections[`${property.id}-pending`] ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                      </div>

                      {/* Pending Cleaners Content */}
                      {expandedSections[`${property.id}-pending`] && (
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="py-3 px-4 text-left font-medium text-gray-700">CLEANER</th>
                              <th className="py-3 px-4 text-left font-medium text-gray-700">EMAIL</th>
                              <th className="py-3 px-4 text-left font-medium text-gray-700">STATUS</th>
                              {/* <th className="py-3 px-4 text-left font-medium text-gray-700">ACTIONS</th> */}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {property.pendingCleaners.map((cleaner, index) => (
                              <tr key={index}>
                                <td className="py-4 px-4">
                                  <div className="font-medium text-gray-900">
                                    {cleaner.first_name} {cleaner.surname}
                                  </div>
                                  <div className="text-sm text-gray-500 mt-1">
                                    {/* Invited: {new Date().toISOString().split('T')[0]} */}
                                  </div>
                                </td>
                                <td className="py-4 px-4 text-gray-600">{cleaner.email}</td>
                                <td className="py-4 px-4">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(cleaner.status)}`}>
                                    {cleaner.status}
                                  </span>
                                </td>
                                {/* <td className="py-4 px-4">
                                  <button className="text-gray-400 hover:text-gray-600">
                                    ☑
                                  </button>
                                </td> */}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* No Cleaners Message */}
                  {property.acceptedCleanersCount === 0 && property.pendingCleanersCount === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No cleaner in this property yet
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="border border-[#DFDFDF] bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-lg font-bold text-[#586989]">No Properties found</p>
            <p className="text-gray-600 mt-2">You don't have any properties with cleaners assigned yet.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default Cleaners;